import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";
import { useSpvPoolList } from "@/hooks/capitalMarkets/useSpvPools";
import { useSpvTrustList } from "@/hooks/capitalMarkets/useSpvTrusts";
import { SPV_POOL_STATUSES, type SpvPoolDto } from "@/types/spvPool";
import { PoolStatusBadge, KpiCard } from "../shared/components";
import {
  downloadCsv,
  formatCurrency,
  formatDate,
  humanizeStatus,
} from "../shared/format";

export function PoolListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const tz = useUserTimeZone();
  const canBuild = hasPermission("SpvPool.Build");

  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const poolsQuery = useSpvPoolList({ pageSize: 200 });
  const trustsQuery = useSpvTrustList({ pageSize: 200 });

  const pools = useMemo(() => poolsQuery.data?.data ?? [], [poolsQuery.data]);
  const trusts = trustsQuery.data?.data ?? [];

  const trustName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of trusts) map.set(t.id, t.trustName);
    return map;
  }, [trusts]);

  const filtered = useMemo(
    () => (statusFilter ? pools.filter((p) => p.status === statusFilter) : pools),
    [pools, statusFilter],
  );

  const kpis = useMemo(() => {
    const activeCount = pools.filter((p) => p.status === "Active").length;
    const totalTarget = pools.reduce((s, p) => s + (p.targetPoolSize ?? 0), 0);
    return { activeCount, totalTarget, poolCount: pools.length };
  }, [pools]);

  const columns: Column<SpvPoolDto>[] = useMemo(
    () => [
      {
        key: "poolName",
        header: "Pool Name",
        render: (p) => (
          <Link
            to={`/capital-markets/pools/${p.id}`}
            className="font-semibold hover:text-gold-shadow"
          >
            {p.poolName}
          </Link>
        ),
      },
      {
        key: "trust",
        header: "SPV Trust",
        render: (p) => trustName.get(p.spvId) ?? "—",
      },
      {
        key: "status",
        header: "Status",
        render: (p) => <PoolStatusBadge status={p.status} />,
      },
      {
        key: "inception",
        header: "Inception Date",
        render: (p) => formatDate(p.inceptionDate),
      },
      {
        key: "target",
        header: "Target Size",
        render: (p) => formatCurrency(p.targetPoolSize),
      },
      {
        key: "closing",
        header: "Closing Date",
        render: (p) => formatDate(p.closingDate),
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (p) => (
          <Link
            to={`/capital-markets/pools/${p.id}`}
            className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline"
          >
            View
          </Link>
        ),
      },
    ],
    [trustName],
  );

  const handleExport = () => {
    const header = [
      "Pool Name",
      "SPV Trust",
      "Status",
      "Inception Date",
      "Target Size",
      "Min Segments",
      "Closing Date",
    ];
    const rows = filtered.map((p) => [
      p.poolName,
      trustName.get(p.spvId) ?? "",
      humanizeStatus(p.status),
      p.inceptionDate ?? "",
      String(p.targetPoolSize ?? ""),
      String(p.minSegmentCount ?? ""),
      p.closingDate ?? "",
    ]);
    downloadCsv(
      `spv-pools-${isoDateInTz(new Date(), tz)}.csv`,
      [header, ...rows],
    );
  };

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title="SPV Pools"
        description="Pool formation, composition, contribution and investor placement."
        actions={
          <div className="flex items-center gap-space-3">
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
            </Button>
            {canBuild && (
              <Link to="/capital-markets/pools/new">
                <Button>
                  <Plus size={16} strokeWidth={2} aria-hidden /> New Pool
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-3 mb-space-4">
        <KpiCard label="Active Pools" value={kpis.activeCount} />
        <KpiCard label="Total Pools" value={kpis.poolCount} />
        <KpiCard
          label="Aggregate Target Size"
          value={formatCurrency(kpis.totalTarget)}
        />
        <KpiCard
          label="Statuses In Flight"
          value={
            new Set(pools.filter((p) => p.status !== "WoundDown").map((p) => p.status))
              .size
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
        <FilterChip
          label="All"
          active={statusFilter === null}
          onClick={() => setStatusFilter(null)}
        />
        {SPV_POOL_STATUSES.map((s) => (
          <FilterChip
            key={s}
            label={humanizeStatus(s)}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          />
        ))}
      </div>

      <div
        role="presentation"
        onClick={(e) => {
          const tr = (e.target as HTMLElement).closest("tr[data-pool-id]");
          if (tr) navigate(`/capital-markets/pools/${tr.getAttribute("data-pool-id")}`);
        }}
      >
        <DataTable
          columns={columns}
          rows={filtered}
          loading={poolsQuery.isLoading}
          error={poolsQuery.isError ? "Could not load SPV pools." : null}
          emptyMessage="No SPV pools yet. Create your first pool to begin."
          rowKey={(p) => p.id}
        />
      </div>
    </>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-1 rounded-full text-caption font-display uppercase tracking-wide border",
        active
          ? "bg-gold text-onyx border-gold font-semibold"
          : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
      )}
    >
      {label}
    </button>
  );
}
